import { useState } from 'react';
import { Link } from 'react-router-dom';
import styles from '../GenerationWorkspace.module.css';
import { brandConfig } from '../../../config/brands';
import type { AttributeTreeNode, GenerationAsset, GenerationSettings } from '../state/types';
import type { Brand } from '../../../features/brands/types';
import type { RecommendedKeys } from '../../common/HierarchicalSelect';
import { Tooltip } from '../../common/Tooltip';
import { ToggleSwitch } from '../../common/ToggleSwitch';
import { useMaterialsPanelStore } from '../../../stores/materialsPanelStore';
import { AttributeNodeEditor } from './AttributeNodeEditor';
import { BannerLayoutEntry } from '../bannerLayout/BannerLayoutEntry';
import { isBannerLayoutNode } from '../../../features/bannerLayout/attributeNode';

/**
 * 背景・構図セクション。テンプレート適用中は閉じて操作不可にし、注記をアコーディオンに重ねる。
 * GenerationWorkspace の JSX からの切り出しで、DOM 構造・クラス名は不変。
 */
export function CompositionSection({
  order,
  attributeTree,
  settings,
  setSettings,
  onAttributeChange,
  recommendedKeysByGroup,
  compositionSummary,
  requiresProductInfoEffectLabel,
  selectedAssets,
  hasAnyProductLinks,
  brandLogosAvailable,
  extractedAsins,
  brandsWithoutLogos,
  selectedBackgroundRequiresLogo,
  closeMaterialsPanel,
  isTemplateActive,
  templateLockClass,
  renderOpen,
  onToggle,
  onClose,
  bannerLayoutEnabled,
}: {
  order: number;
  attributeTree: AttributeTreeNode[];
  settings: GenerationSettings;
  setSettings: (next: GenerationSettings | ((prev: GenerationSettings) => GenerationSettings)) => void;
  onAttributeChange: (key: string, value: unknown) => void;
  recommendedKeysByGroup: Record<string, RecommendedKeys> | undefined;
  compositionSummary: string;
  requiresProductInfoEffectLabel: string | null;
  selectedAssets: GenerationAsset[];
  hasAnyProductLinks: boolean;
  brandLogosAvailable: boolean;
  extractedAsins: string[];
  brandsWithoutLogos: Brand[];
  selectedBackgroundRequiresLogo: boolean;
  closeMaterialsPanel: () => void;
  isTemplateActive: boolean;
  templateLockClass: string;
  /** テンプレート適用中は実 state を保持したまま閉じて見せる (compositionRenderOpen) */
  renderOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
  /**
   * バナーレイアウトの入口を描くか (公開フラグ・親が判定)。
   * 入口の位置は属性ツリーの「レイアウト」ノード (isBannerLayoutNode・composition 直下の固定フィールド) が決める:
   * sort_order / placement に従い、パートナーが非表示にしていればツリーに無いので描かれない (§19)
   */
  bannerLayoutEnabled?: boolean;
}) {
  // 詳細設定 (placement='advanced' のフィールド) の開閉。既定は閉じる。
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);

  const compositionNode = (attributeTree ?? []).find((n) => n.key === 'composition');
  const sortedChildren = [...(compositionNode?.children ?? [])]
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
    // 「レイアウト」ノードは公開フラグが無効なら描かない (選択肢を持たないので AttributeNodeEditor でも何も出ない)
    .filter((n) => bannerLayoutEnabled || !isBannerLayoutNode(n));
  const primaryChildren = sortedChildren.filter((n) => n.placement !== 'advanced');
  const advancedChildren = sortedChildren.filter((n) => n.placement === 'advanced');

  const renderAttributeNode = (node: AttributeTreeNode) => {
    // 「レイアウト」(固定フィールド): 選択肢の代わりにバナーレイアウトの入口。ラベルはノード (パートナーの上書き込み) から
    if (isBannerLayoutNode(node)) {
      return (
        <BannerLayoutEntry
          key={node.id}
          label={node.label}
          settings={settings}
          setSettings={setSettings}
          selectedAssets={selectedAssets}
        />
      );
    }
    // エフェクトのみ、商品情報不足の警告 UI を添える
    if (node.key === 'effect') {
      return (
        <div key={node.id}>
          <AttributeNodeEditor
            node={node}
            value={settings.attributes?.[node.key]}
            onChange={onAttributeChange}
            recommendedKeysByGroup={recommendedKeysByGroup}
          />
          {requiresProductInfoEffectLabel && selectedAssets.length > 0 && !hasAnyProductLinks && (
            <div style={{ margin: '8px 0 0 0', paddingLeft: '12px', fontSize: '11px', color: 'rgba(255, 180, 60, 0.85)' }}>
              <p style={{ margin: 0 }}>
                「{requiresProductInfoEffectLabel}」の実行には、以下の素材に関連{brandConfig.labels.productShort}を設定する必要があります。
              </p>
              <div style={{ display: 'flex', gap: '6px', marginTop: '4px', flexWrap: 'wrap' }}>
                {selectedAssets.map((asset) => (
                  <button
                    key={asset.id}
                    type="button"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: '0',
                      background: 'transparent',
                      border: '1px solid rgba(255, 180, 60, 0.4)',
                      borderRadius: '4px',
                      cursor: 'pointer',
                    }}
                    onClick={() => {
                      useMaterialsPanelStore.getState().openMaterialDetail(asset.id);
                    }}
                  >
                    <img
                      src={asset.thumbnailUrl}
                      alt=""
                      style={{ width: '28px', height: '28px', objectFit: 'contain', borderRadius: '3px' }}
                    />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      );
    }
    return (
      <AttributeNodeEditor
        key={node.id}
        node={node}
        value={settings.attributes?.[node.key]}
        onChange={onAttributeChange}
        recommendedKeysByGroup={recommendedKeysByGroup}
      />
    );
  };

  return (
    <div className={styles.accordionLockWrap} style={{ order }}>
      {isTemplateActive && (
        <div className={styles.templateDisabledOverlay}>テンプレート適用中は利用できません</div>
      )}
    <div
      className={`accordion-section ${renderOpen ? 'accordion-section-open' : ''}${templateLockClass}`}
    >
      <button
        type="button"
        className="accordion-header"
        onClick={onToggle}
        aria-expanded={renderOpen}
      >
        <span className="accordion-title">
          <img src="/composition_focus_square.svg" alt="" className="accordion-title-icon" />
          <span>背景・構図</span>
          {!!requiresProductInfoEffectLabel && selectedAssets.length > 0 && !hasAnyProductLinks && (
            <svg width="15" height="14" viewBox="0 0 15 14" fill="none" style={{ marginLeft: '4px', flexShrink: 0 }} aria-label={`${brandConfig.labels.productShort}情報の設定が必要です`}>
              <title>{brandConfig.labels.productShort}情報の設定が必要です</title>
              <path d="M6.27 1.5a1.5 1.5 0 0 1 2.46 0l5.1 8.5A1.5 1.5 0 0 1 12.6 12.5H2.4A1.5 1.5 0 0 1 1.17 10l5.1-8.5Z" fill="rgba(255,180,60,0.9)" />
              <text x="7.5" y="10.5" textAnchor="middle" fontSize="9" fontWeight="700" fill="#1a1a1a">!</text>
            </svg>
          )}
          <span className="accordion-summary">{compositionSummary}</span>
        </span>
        <span className={`accordion-icon ${renderOpen ? 'accordion-icon-open' : ''}`}>
          {renderOpen ? '▼' : '▶'}
        </span>
      </button>
      <div className={`accordion-panel ${renderOpen ? 'accordion-panel-open' : 'accordion-panel-closed'}`}>
        <div className={`${styles.attributeGrid} ${styles.attributeGridComposition}`}>
          {/* tree 駆動: 背景・構図(composition)配下の子ノードをそのまま描画する。
              管理画面で追加したグループ(カメラアングル等)も sort_order 順に表示される。
              ハードコードのキー列挙はしない。placement='advanced' のフィールドは
              下の「詳細設定」エキスパンド内に出す。 */}
          {primaryChildren.map(renderAttributeNode)}
        </div>
        {advancedChildren.length > 0 && (
          <div className={styles.compositionAdvancedSection}>
            <button
              type="button"
              onClick={() => setIsAdvancedOpen(!isAdvancedOpen)}
              aria-expanded={isAdvancedOpen}
              className={styles.compositionAdvancedToggle}
            >
              <svg
                width="8"
                height="8"
                viewBox="0 0 8 8"
                fill="currentColor"
                style={{ transition: 'transform 0.2s', transform: isAdvancedOpen ? 'rotate(90deg)' : 'none' }}
              >
                <path d="M2 0l4 4-4 4z" />
              </svg>
              <span>詳細設定</span>
            </button>
            {isAdvancedOpen && (
              <div className={`${styles.attributeGrid} ${styles.attributeGridComposition}`}>
                {advancedChildren.map(renderAttributeNode)}
              </div>
            )}
          </div>
        )}
        {/* ロゴ追加トグルと「閉じる」を同じ行に配置。選択中の素材からロゴが1つも解決できないときはトグルを出さない
            (プロンプト入力モードの同じトグルと揃える: GenerationWorkspace の promptFooter) */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          {brandLogosAvailable && (
            <ToggleSwitch
              checked={settings.include_logo ?? false}
              onChange={(checked) => {
                setSettings((prev) => ({ ...prev, include_logo: checked }));
              }}
              label="ロゴを追加する"
            />
          )}
          {/* 個別ロゴのサムネ表示は廃止。トグルON時はバックエンドが
              登録ロゴを1枚にまとめた「ロゴシート」を添付する。 */}
          {!brandLogosAvailable && extractedAsins.length > 0 && brandsWithoutLogos.length > 0 && (
            <p style={{ margin: '4px 0 0 24px', fontSize: '11px', color: 'rgba(255, 255, 255, 0.4)' }}>
              ブランド「
              {brandsWithoutLogos.map((brand, index) => (
                <span key={brand.id}>
                  {index > 0 && '、'}
                  <Link
                    to={`/brands/${brand.id}`}
                    onClick={closeMaterialsPanel}
                    style={{ color: 'rgba(255, 255, 255, 0.6)' }}
                  >
                    {brand.brand_name}
                  </Link>
                </span>
              ))}
              」にロゴが設定されていません。
            </p>
          )}
          {selectedBackgroundRequiresLogo && !brandLogosAvailable && (
            <p style={{ margin: '4px 0 0 0', fontSize: '11px', color: '#f59e0b' }}>
              ※ この背景はブランドロゴの登録が推奨されます
            </p>
          )}
        </div>
        {/* 思考レベルは「モデル」選択(MediaRequirementsAccordion)の直下に移動し、
            Google Gemini スピード選択時のみ表示する */}
        <div className={styles.accordionPanelFooter} style={{ marginTop: 0, flexShrink: 0 }}>
          <Tooltip content="閉じる">
            <button
              type="button"
              className={styles.iconButton}
              onClick={onClose}
            >
              <img src="/move_collapse_vertical.svg" alt="閉じる" style={{ width: '16px', height: '16px' }} />
            </button>
          </Tooltip>
        </div>
        </div>
      </div>
    </div>
    </div>
  );
}
